import cx from 'classnames'

import { ReactComponent as HandbookIcon } from 'assets/icons/handbook_16--solid.svg'
import { FolderIcon as NormalFolderIcon } from 'components/Icons/FolderIcon'
import { LockedFolderIcon as LockedIcon } from 'components/Icons/FolderIcon'
import { MentionIcon } from 'components/MentionIcon/MentionIcon'
import { externalFileEmoji } from 'components/ReviewsWizard/FileSelection/urlValidationUtils'

import Styles from './FileIcon.module.scss'
import { FileIconProps } from './Types'
import { ChannelType } from 'components/CampaignCreationModal/Types'
import { Video } from '../views/GalleryView/icons/Video/Video'
import { Audio } from '../views/GalleryView/icons/Audio/Audio'
import { ChannelIcon } from 'components/ChannelIcon/ChannelIcon'
import * as CSS from 'csstype'
import { Document, Design, Folder, FolderOpen2 } from 'blaze-ui/icons'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { getBlogPlatformBrandingWithOnboardingSelection } from 'screens/Calendar/WeekViewV2/CalendarEventCard/utils'
import { useBlogIntegrations } from 'hooks/useBlogIntegrations'
import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'

export const EmojiIcon = ({ emoji, className }: { emoji: string; className?: string }) => (
  <span className={className}>{emoji}</span>
)

export const CommonFolderIcon = ({ className }: { className?: string }) => (
  <NormalFolderIcon className={cx(Styles.icon, className)} />
)

export const LockedFolderIcon = ({ className }: { className?: string }) => (
  <LockedIcon className={cx(Styles.lockedFolderIcon, className)} />
)

export const FileIcon = ({ file, size = 'sm', className, color, isExpanded, brandPlan }: FileIconProps) => {
  const { mime_type, type, header_emoji, channel, channel_emoji, external_integration_id } = file
  const wixEnabled = useFlipperFlag('wix_blog_integration')
  const blogPlatformOnboardingBrandingEnabled = useFlipperFlag('blog_platform_onboarding_branding')
  const { connectedBlogIntegration } = useBlogIntegrations()
  const channelEmoji = channel_emoji || channel
  const isFolder = type === 'folder'
  const isImage = mime_type?.startsWith('image')
  const isVideo = mime_type?.startsWith('video')
  const isAudio = mime_type?.startsWith('audio')
  const isDesign = type === 'design'

  let iconSize = 16
  if (size === 'md-ap') iconSize = 18
  if (size === 'md') iconSize = 20
  if (size === 'lg') iconSize = 24

  const classNames = cx(Styles.iconContainer, Styles[size], className)
  const style: CSS.Properties = {
    ['--icon-color' as any]: color || 'var(--dark-80)',
  }

  const externalFileIcon = externalFileEmoji(file)
  if (!!externalFileIcon)
    return (
      <div className={classNames} style={style}>
        <div className={Styles.icon}>{externalFileIcon}</div>
        {file.mention_id && <MentionIcon className={Styles.subIcon} />}
      </div>
    )

  if (isFolder)
    return (
      <div className={classNames} style={style}>
        {isExpanded ? <FolderOpen2 size={iconSize} /> : <Folder size={iconSize} />}
      </div>
    )

  if (header_emoji) {
    return (
      <div className={classNames} style={style}>
        <EmojiIcon emoji={header_emoji} className={Styles.emoji} />
        {file.mention_id && <MentionIcon className={Styles.subIcon} />}
      </div>
    )
  }

  if (isImage) {
    return <img className={Styles.mediaFile} src={file.source_url} alt={file.title} />
  }

  if (isVideo) {
    return (
      <div className={classNames} style={style}>
        <Video size={iconSize} />
      </div>
    )
  }

  if (isAudio) {
    return (
      <div className={classNames} style={style}>
        <Audio size={iconSize} />
      </div>
    )
  }

  if (channelEmoji && (channelEmoji as string) !== 'null') {
    // Determine blog platform branding once for efficiency
    const blogPlatformBranding = getBlogPlatformBrandingWithOnboardingSelection({
      channel: channelEmoji,
      externalIntegrationId: external_integration_id,
      externalIntegrations: connectedBlogIntegration ? [connectedBlogIntegration] : [],
      selectedBlogPlatform: brandPlan?.selected_blog_platform,
      featureFlags: {
        wixEnabled,
        blogPlatformOnboardingBrandingEnabled,
      },
    })

    return (
      <div className={classNames} style={style}>
        <span className={Styles.channelEmoji}>
          <ChannelIcon
            channel={channelEmoji as ChannelType}
            size={iconSize}
            useGoHighLevelBranding={blogPlatformBranding === ExternalIntegrationEnum.GoHighLevel}
            useWordPressBranding={blogPlatformBranding === ExternalIntegrationEnum.WordPress}
          />
        </span>
        {file.mention_id && <MentionIcon className={Styles.subIcon} />}
      </div>
    )
  }

  if (isDesign) {
    return (
      <div className={cx(classNames, Styles.imageContainer)}>
        <Design size={iconSize} />
      </div>
    )
  }

  if (type === 'handbook') {
    return <HandbookIcon className={Styles.handbookIcon} style={{ fill: file.header_emoji_color ?? '#EDB62C' }} />
  }

  return (
    <div className={classNames} style={style}>
      <Document size={iconSize} />
      {file.mention_id && file.has_edit_access && <MentionIcon className={Styles.subIcon} />}
    </div>
  )
}
